import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchCompanies, fetchProformas, fetchProforma, createProforma, updateProformaStatus, recordProformaPayment, getCurrency,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — B-1: proforma quotes. A priced quote a hotel gives a company
// before payment. Totals are computed server-side (service charge + optional
// VAT + optional 3% withholding). Non-fiscal number (PF…). Printable.
const S = {
  en: {
    title: "Proformas", subtitle: "Priced quotes for companies — rooms, events, packages",
    back: "Back to companies", company: "Company (optional)", none_company: "— No company —",
    quote_title: "Quote title / event", desc: "Description", qty: "Pax / Qty", price: "Unit price",
    ref_no: "REF.NO.", prep_date: "Prepared", service_date: "Service start",
    add_line: "+ Add line", remove: "Remove", apply_vat: "Add VAT", apply_wh: "Company withholds 3%",
    notes: "Notes (optional)", create: "Create proforma", creating: "Creating…",
    subtotal: "Subtotal", service: "Service charge", vat: "VAT", total: "Total",
    withholding: "Withholding", net: "Net payable", status: "Status",
    print: "Print", mark_sent: "Mark sent", mark_paid: "Mark paid",
    list_title: "Recent proformas", none: "No proformas yet. Create one above.",
    line_req: "Add at least one line with a description and price.", err: "Could not create proforma.",
    draft: "Draft", sent: "Sent", paid: "Paid", void: "Void",
    tin: "TIN", licence: "Licence", prepared_for: "Prepared for", issued_by: "Issued by",
    proforma_doc: "PROFORMA INVOICE", date: "Date", quote_no: "Proforma no.",
    record_payment: "Record payment", payment_recorded: "Payment recorded",
    pay_title: "Record company payment", pay_amount: "Amount received", pay_method: "Method (optional)", pay_note: "Note (optional)",
    pay_hint: "Enter what the company actually paid — it can differ from the quote. This adds a deposit to the company's account.",
    save: "Save", cancel: "Cancel",
    pay_err_recorded: "A payment has already been recorded from this proforma.", pay_err: "Could not record the payment.",
  },
  am: {
    title: "ፕሮፎርማዎች", subtitle: "ለድርጅቶች የዋጋ ጥቅሶች — ክፍሎች፣ ዝግጅቶች፣ ጥቅሎች",
    back: "ወደ ድርጅቶች ተመለስ", company: "ድርጅት (አማራጭ)", none_company: "— ድርጅት የለም —",
    quote_title: "የጥቅስ ርዕስ / ዝግጅት", desc: "መግለጫ", qty: "ተሳታፊ / ብዛት", price: "የነጠላ ዋጋ",
    ref_no: "ማጣቀሻ ቁ.", prep_date: "የተዘጋጀበት", service_date: "አገልግሎት ጅምር",
    add_line: "+ መስመር ጨምር", remove: "አስወግድ", apply_vat: "ተ.እ.ታ ጨምር", apply_wh: "ድርጅቱ 3% ይይዛል",
    notes: "ማስታወሻ (አማራጭ)", create: "ፕሮፎርማ ፍጠር", creating: "በመፍጠር ላይ…",
    subtotal: "ንዑስ ድምር", service: "የአገልግሎት ክፍያ", vat: "ተ.እ.ታ", total: "ጠቅላላ",
    withholding: "ተቀናሽ ግብር", net: "የተጣራ የሚከፈል", status: "ሁኔታ",
    print: "አትም", mark_sent: "እንደተላከ ምልክት", mark_paid: "እንደተከፈለ ምልክት",
    list_title: "የቅርብ ፕሮፎርማዎች", none: "እስካሁን ፕሮፎርማ የለም። ከላይ አንድ ይፍጠሩ።",
    line_req: "ቢያንስ አንድ መስመር ከመግለጫና ዋጋ ጋር ይጨምሩ።", err: "ፕሮፎርማ መፍጠር አልተቻለም።",
    draft: "ረቂቅ", sent: "ተልኳል", paid: "ተከፍሏል", void: "ተሰርዟል",
    tin: "ቲን", licence: "ፈቃድ", prepared_for: "ለ", issued_by: "አዘጋጅ",
    proforma_doc: "ፕሮፎርማ ደረሰኝ", date: "ቀን", quote_no: "የፕሮፎርማ ቁ.",
    record_payment: "ክፍያ መዝግብ", payment_recorded: "ክፍያ ተመዝግቧል",
    pay_title: "የድርጅት ክፍያ መዝግብ", pay_amount: "የተቀበለው መጠን", pay_method: "ዘዴ (አማራጭ)", pay_note: "ማስታወሻ (አማራጭ)",
    pay_hint: "ድርጅቱ በትክክል የከፈለውን ያስገቡ — ከጥቅሱ ሊለያይ ይችላል። ይህ ወደ ድርጅቱ ሂሳብ ተቀማጭ ይጨምራል።",
    save: "አስቀምጥ", cancel: "ሰርዝ",
    pay_err_recorded: "ከዚህ ፕሮፎርማ ክፍያ አስቀድሞ ተመዝግቧል።", pay_err: "ክፍያውን መመዝገብ አልተቻለም።",
  },
  om: {
    title: "Proformaa", subtitle: "Tilmaama gatii dhaabbilee — kutaalee, sagantaalee, paakejii",
    back: "Gara dhaabbilee deebi'i", company: "Dhaabbata (filannoo)", none_company: "— Dhaabbanni hin jiru —",
    quote_title: "Mata-duree tilmaamaa / sagantaa", desc: "Ibsa", qty: "Hirmaattota / Baay'ina", price: "Gatii tokkoo",
    ref_no: "Lakk. wabii", prep_date: "Kan qophaa'e", service_date: "Jalqaba tajaajilaa",
    add_line: "+ Sarara dabali", remove: "Haqi", apply_vat: "VAT dabali", apply_wh: "Dhaabbatichi 3% qabata",
    notes: "Yaadannoo (filannoo)", create: "Proformaa uumi", creating: "Uumaa jira…",
    subtotal: "Ida'ama xiqqaa", service: "Kaffaltii tajaajilaa", vat: "VAT", total: "Ida'ama",
    withholding: "Gibira qabame", net: "Qulqulluu kaffalamu", status: "Haala",
    print: "Maxxansi", mark_sent: "Erge jedhii", mark_paid: "Kaffaleera jedhii",
    list_title: "Proformaa dhiyoo", none: "Ammatti proformaan hin jiru. Ol tokko uumi.",
    line_req: "Yoo xiqqaate sarara tokko ibsaa fi gatii waliin dabali.", err: "Proformaa uumuu hin dandeenye.",
    draft: "Wixinee", sent: "Erge", paid: "Kaffalame", void: "Haqame",
    tin: "TIN", licence: "Hayyama", prepared_for: "Kan qophaa'e", issued_by: "Kan baase",
    proforma_doc: "PROFORMAA INVOOYISII", date: "Guyyaa", quote_no: "Lakk. proformaa",
    record_payment: "Kaffaltii galmeessi", payment_recorded: "Kaffaltiin galmaa'e",
    pay_title: "Kaffaltii dhaabbataa galmeessi", pay_amount: "Hanga fudhatame", pay_method: "Mala (filannoo)", pay_note: "Yaadannoo (filannoo)",
    pay_hint: "Wanta dhaabbatichi dhugumatti kaffale galchi — tilmaama irraa adda ta'uu danda'a. Kun herrega dhaabbatichaatti qarshii dabala.",
    save: "Olkaa'i", cancel: "Haqi",
    pay_err_recorded: "Proformaa kana irraa kaffaltiin duraan galmaa'eera.", pay_err: "Kaffaltii galmeessuu hin dandeenye.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };
const emptyLine = { desc: "", qty: "1", unit_price: "" };

export default function ProformasPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";

  const [companies, setCompanies] = useState([]);
  const [rows, setRows] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [title, setTitle] = useState("");
  const [refNo, setRefNo] = useState("");
  const [prepDate, setPrepDate] = useState(new Date().toISOString().slice(0, 10));
  const [serviceDate, setServiceDate] = useState("");
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [applyVat, setApplyVat] = useState(false);
  const [applyWh, setApplyWh] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => fetchProformas().then(setRows).catch(() => setRows([]));
  useEffect(() => {
    fetchCompanies().then(setCompanies).catch(() => setCompanies([]));
    load();
  }, []);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, { ...emptyLine }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));

  const preview = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);

  const submit = async () => {
    setErr("");
    const clean = lines
      .filter((l) => l.desc.trim() && Number(l.unit_price) >= 0 && Number(l.qty) > 0)
      .map((l) => ({ desc: l.desc.trim(), qty: Number(l.qty), unit_price: Number(l.unit_price) }));
    if (clean.length === 0) { setErr(t("line_req")); return; }
    setBusy(true);
    try {
      await createProforma({
        ...(companyId ? { company_account_id: Number(companyId) } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(refNo.trim() ? { ref_no: refNo.trim() } : {}),
        ...(prepDate ? { preparation_date: prepDate } : {}),
        ...(serviceDate ? { service_date: serviceDate } : {}),
        lines: clean, apply_vat: applyVat, apply_withholding: applyWh,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      setTitle(""); setRefNo(""); setServiceDate(""); setPrepDate(new Date().toISOString().slice(0, 10));
      setLines([{ ...emptyLine }]); setApplyVat(false); setApplyWh(false); setNotes("");
      load();
    } catch { setErr(t("err")); }
    finally { setBusy(false); }
  };

  // Record-payment modal (deposit into the linked company account).
  const [payFor, setPayFor] = useState(null);   // the proforma being paid
  const [payForm, setPayForm] = useState({ amount: "", method: "", note: "" });
  const [payErr, setPayErr] = useState("");
  const [paySaving, setPaySaving] = useState(false);
  const openPay = (p) => {
    setPayErr("");
    setPayForm({ amount: String(p.net_payable || p.total || ""), method: "", note: "" });
    setPayFor(p);
  };
  const submitPay = async () => {
    setPayErr("");
    const amt = Number(payForm.amount);
    if (!(amt > 0)) { setPayErr(t("pay_err")); return; }
    setPaySaving(true);
    try {
      const res = await recordProformaPayment(payFor.id, { amount: amt, method: payForm.method || null, note: payForm.note || null });
      const pf = res.proforma;
      setRows((rs) => rs.map((x) => (x.id === payFor.id ? { ...x, status: pf.status, deposit_recorded: true, deposit_ledger_id: pf.deposit_ledger_id } : x)));
      setPayFor(null);
    } catch (e) {
      const code = e && e.response && e.response.data && e.response.data.detail && e.response.data.detail.code;
      setPayErr(code === "already_recorded" ? t("pay_err_recorded") : t("pay_err"));
    } finally { setPaySaving(false); }
  };

  const setStatus = async (p, status) => {
    setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, status } : x)));
    try { await updateProformaStatus(p.id, status); } catch { load(); }
  };

  const printProforma = async (p) => {
    let d;
    try { d = await fetchProforma(p.id); } catch { return; }
    const h = d.hotel || {}; const c = d.company || {};
    const money = (n) => formatPrice(n || 0, cur);
    const rowsHtml = (d.lines || []).map((l, i) => `<tr>
      <td>${i + 1}</td><td>${(l.desc || "").replace(/</g, "&lt;")}</td>
      <td style="text-align:right">${l.qty}</td>
      <td style="text-align:right">${money(l.unit_price)}</td>
      <td style="text-align:right">${money((l.qty || 0) * (l.unit_price || 0))}</td></tr>`).join("");
    const issuer = h.legal_name || h.name || "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${d.number}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:32px;font-size:13px}
      h1{font-size:20px;margin:0 0 4px}.muted{color:#666}
      .row{display:flex;justify-content:space-between;gap:24px;margin:14px 0}
      table{width:100%;border-collapse:collapse;margin-top:12px}
      th,td{border:1px solid #bbb;padding:6px 8px}th{background:#f0f0f0;text-align:left}
      .tot{margin-top:12px;margin-left:auto;width:280px}
      .tot div{display:flex;justify-content:space-between;padding:3px 0}
      .tot .g{font-weight:bold;border-top:1px solid #333;margin-top:4px;padding-top:6px}</style></head><body>
      <h1>${t("proforma_doc")}</h1>
      <div class="muted">${t("quote_no")}: <b>${d.number}</b>${d.ref_no ? ` · ${t("ref_no")} <b>${(d.ref_no).replace(/</g, "&lt;")}</b>` : ""} · ${t("prep_date")}: ${d.preparation_date || new Date().toISOString().slice(0, 10)}${d.service_date ? ` · ${t("service_date")}: ${d.service_date}` : ""} · ${t("status")}: ${t(d.status) || d.status}</div>
      ${d.title ? `<div style="margin-top:6px"><b>${(d.title).replace(/</g, "&lt;")}</b></div>` : ""}
      <div class="row">
        <div><div class="muted">${t("issued_by")}</div><b>${issuer}</b>
          ${h.tin ? `<div>${t("tin")}: ${h.tin}</div>` : ""}${h.vat_reg_no ? `<div>VAT: ${h.vat_reg_no}</div>` : ""}
          ${h.address ? `<div>${h.address}</div>` : ""}${h.phone ? `<div>${h.phone}</div>` : ""}</div>
        <div><div class="muted">${t("prepared_for")}</div><b>${c.name || "—"}</b>
          ${c.tin ? `<div>${t("tin")}: ${c.tin}</div>` : ""}${c.business_license_no ? `<div>${t("licence")}: ${c.business_license_no}</div>` : ""}
          ${c.address ? `<div>${c.address}</div>` : ""}</div>
      </div>
      <table><thead><tr><th>#</th><th>${t("desc")}</th><th style="text-align:right">${t("qty")}</th>
        <th style="text-align:right">${t("price")}</th><th style="text-align:right">${t("total")}</th></tr></thead>
        <tbody>${rowsHtml}</tbody></table>
      <div class="tot">
        <div><span>${t("subtotal")}</span><span>${money(d.subtotal)}</span></div>
        ${d.service_charge_amount ? `<div><span>${t("service")} (${d.service_charge_pct}%)</span><span>${money(d.service_charge_amount)}</span></div>` : ""}
        ${d.apply_vat ? `<div><span>${t("vat")} (${d.vat_pct}%)</span><span>${money(d.vat_amount)}</span></div>` : ""}
        <div class="g"><span>${t("total")}</span><span>${money(d.total)}</span></div>
        ${d.apply_withholding ? `<div><span>${t("withholding")} (${d.withholding_pct}%)</span><span>- ${money(d.withholding_amount)}</span></div>
        <div class="g"><span>${t("net")}</span><span>${money(d.net_payable)}</span></div>` : ""}
      </div>
      ${d.notes ? `<div class="muted" style="margin-top:14px">${(d.notes).replace(/</g, "&lt;")}</div>` : ""}
      <div class="muted" style="margin-top:24px">This is a proforma quotation, not a fiscal invoice.</div>
      </body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* ignore */ } }, 300);
  };

  const badge = (s) => ({ draft: "#8a949b", sent: "#0C447C", paid: "#1D7A44", void: "#A32D2D" }[s] || "#8a949b");

  return (
    <div style={{ maxWidth: 940, margin: "0 auto" }}>
      <Link className="muted" to="/front-desk/companies" style={{ fontSize: 13, textDecoration: "none" }}>← {t("back")}</Link>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* Builder */}
      <div style={{ ...CARD, padding: 16, marginTop: 14 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={{ ...inp, minWidth: 200 }}>
            <option value="">{t("none_company")}</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("quote_title")} style={{ ...inp, flex: 1, minWidth: 220 }} />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <input value={refNo} onChange={(e) => setRefNo(e.target.value)} placeholder={t("ref_no")} style={{ ...inp, minWidth: 150 }} />
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("prep_date")}
            <input type="date" value={prepDate} onChange={(e) => setPrepDate(e.target.value)} style={{ ...inp, marginLeft: 4 }} /></label>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("service_date")}
            <input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} style={{ ...inp, marginLeft: 4 }} /></label>
        </div>

        {lines.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
            <input value={l.desc} onChange={(e) => setLine(i, "desc", e.target.value)} placeholder={t("desc")} style={{ ...inp, flex: 1 }} />
            <input value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} placeholder={t("qty")} type="number" min="0" style={{ ...inp, width: 80 }} />
            <input value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} placeholder={t("price")} type="number" min="0" style={{ ...inp, width: 120 }} />
            <span style={{ width: 110, textAlign: "right", fontSize: 13, color: "#5b6472" }}>{formatPrice((Number(l.qty) || 0) * (Number(l.unit_price) || 0), cur)}</span>
            <button onClick={() => removeLine(i)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 16 }}>×</button>
          </div>
        ))}
        <button className="btn secondary small" onClick={addLine} style={{ marginTop: 2 }}>{t("add_line")}</button>

        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
          <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={applyVat} onChange={(e) => setApplyVat(e.target.checked)} /> {t("apply_vat")}
          </label>
          <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={applyWh} onChange={(e) => setApplyWh(e.target.checked)} /> {t("apply_wh")}
          </label>
          <span style={{ marginLeft: "auto", fontSize: 14 }}>{t("subtotal")}: <b>{formatPrice(preview, cur)}</b></span>
        </div>

        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("notes")} style={{ ...inp, width: "100%", marginTop: 10 }} />
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn primary" disabled={busy} onClick={submit}>{busy ? t("creating") : t("create")}</button>
          {err && <span style={{ color: "#A32D2D", fontSize: 13 }}>{err}</span>}
        </div>
      </div>

      {/* List */}
      <h2 style={{ margin: "22px 0 8px", fontSize: 18 }}>{t("list_title")}</h2>
      {rows == null ? (
        <p className="muted">…</p>
      ) : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((p) => (
            <div key={p.id} style={{ ...CARD, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontWeight: 600 }}>{p.number}{p.title ? ` · ${p.title}` : ""}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {t("total")} {formatPrice(p.total, cur)}{p.apply_withholding ? ` · ${t("net")} ${formatPrice(p.net_payable, cur)}` : ""}
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: badge(p.status), textTransform: "uppercase" }}>{t(p.status) || p.status}</span>
              {p.deposit_recorded && (
                <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 10, background: "#e7f6ec", color: "#1D7A44" }}>✓ {t("payment_recorded")}</span>
              )}
              <button className="btn secondary small" onClick={() => printProforma(p)}>🖨 {t("print")}</button>
              {p.status !== "sent" && p.status !== "paid" && <button className="btn secondary small" onClick={() => setStatus(p, "sent")}>{t("mark_sent")}</button>}
              {/* Money action: only for company-linked proformas, and only once. */}
              {p.company_account_id && !p.deposit_recorded && (
                <button className="btn primary small" onClick={() => openPay(p)}>{t("record_payment")}</button>
              )}
              {p.status !== "paid" && !p.deposit_recorded && <button className="btn secondary small" onClick={() => setStatus(p, "paid")}>{t("mark_paid")}</button>}
            </div>
          ))}
        </div>
      )}

      {/* Record-payment modal — posts a deposit to the linked company account (once). */}
      {payFor && (
        <div onClick={() => !paySaving && setPayFor(null)} style={{ position: "fixed", inset: 0, background: "rgba(6,59,72,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ ...CARD, padding: 18, width: 380, maxWidth: "92vw" }}>
            <div style={{ fontWeight: 700, color: "#063b48", fontSize: 16 }}>{t("pay_title")}</div>
            <div className="muted" style={{ fontSize: 12.5, margin: "6px 0 12px" }}>{payFor.number}{payFor.title ? ` · ${payFor.title}` : ""} — {t("total")} {formatPrice(payFor.total, cur)}</div>
            <label style={{ fontSize: 12, color: "#5b6472" }}>{t("pay_amount")}<br />
              <input type="number" min="0" step="any" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} style={{ ...inp, width: "100%", marginTop: 4 }} autoFocus />
            </label>
            <label style={{ fontSize: 12, color: "#5b6472", display: "block", marginTop: 10 }}>{t("pay_method")}<br />
              <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} style={{ ...inp, width: "100%", marginTop: 4 }}>
                <option value="">—</option>
                <option value="bank_transfer">Bank transfer</option>
                <option value="telebirr">Telebirr</option>
                <option value="cbe_birr">CBE Birr</option>
                <option value="cash">Cash</option>
              </select>
            </label>
            <label style={{ fontSize: 12, color: "#5b6472", display: "block", marginTop: 10 }}>{t("pay_note")}<br />
              <input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} style={{ ...inp, width: "100%", marginTop: 4 }} />
            </label>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.5 }}>{t("pay_hint")}</div>
            {payErr && <div style={{ color: "#A32D2D", fontSize: 12.5, marginTop: 8 }}>{payErr}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" }}>
              <button className="btn secondary small" disabled={paySaving} onClick={() => setPayFor(null)}>{t("cancel")}</button>
              <button className="btn primary small" disabled={paySaving} onClick={submitPay}>{paySaving ? "…" : t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
